import { NotesViewMode } from "@/features/notes/types";
import { UserSettings, ToolbarItemSetting } from "@/services/api/fetchers/user-settings";
import { Theme } from "@/types/common";
import { StateCreator } from "zustand";
import { performOptimisticAction } from "@/shared/utils/store";
import { fetchers } from "@/services/api/fetchers";
import { BoundStore } from "./index";
import { initialUserSettings, storage } from "@/shared/utils/local-storage";
import { TodoDatePeriod } from "@/features/todos/types";

const initialSettings: UserSettings = storage.getUserSettings() ?? initialUserSettings;

export interface SettingsSlice {
  settings: {
    app: {
      theme: Theme;
      encryption: boolean;
      onboardingDone: boolean;
    };
    notes: {
      viewMode: NotesViewMode;
      editorToolbarItems: ToolbarItemSetting[];
      ttsLanguage: string;
    };
    todos: {
      filters: {
        period: TodoDatePeriod;
      };
    };
    fetchSettings: () => Promise<UserSettings>;
    updateSettings: (userId: string, settings: Partial<UserSettings>) => Promise<UserSettings | undefined>;
  };
}

const createSettingsSlice: StateCreator<BoundStore, [], [], SettingsSlice> = (set, get) => ({
  settings: {
    ...initialSettings,
    fetchSettings: async () => {
      try {
        const settings = await fetchers.userSettings.getUserSettings();

        set((state) => ({ settings: { ...state.settings, ...settings } }));
        storage.setUserSettings(settings);

        return settings;
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        return get().settings;
      }
    },
    updateSettings: async (userId, settings) => {
      const existingSettings = get().settings;

      return await performOptimisticAction({
        optimisticUpdate: () => set((state) => ({ settings: { ...state.settings, ...settings } })),
        apiCall: async () => {
          storage.setUserSettings({ ...existingSettings, ...settings });

          const updatedSettings = await fetchers.userSettings.updateUserSettings(userId, {
            ...existingSettings,
            ...settings,
          });

          return updatedSettings;
        },
        rollback: () => {
          storage.setUserSettings(existingSettings);

          return set((state) => ({ settings: { ...state.settings, ...existingSettings } }));
        },
      });
    },
  },
});

export default createSettingsSlice;
